// parseDiagnostic — pull structured data out of the markdown the LLM emits.
//
// Reasoning over numbered sections (1-8) and the scoring table lets the
// renderer build a real layout instead of just rendering raw markdown.
// Tolerant by design: missing pieces just come back as null/empty.

// The LLM translates the template's anchor lines into the UI language
// (fr/en/es), so every anchor below matches the three variants.
const TITLE_RE   = /\*\*\s*(?:Nom\s+du\s+cas\s+d['''’]usage|Use[- ]?case\s+name|Nombre\s+del\s+caso\s+de\s+uso)\s*:?\s*\*\*\s*:?\s*([^\n]+)/i;
const TAGS_RE    = /^(?:Tags?|Etiquetas)\s*:\s*([^\n]+)$/im;
const TAG_TOKEN  = /#([\w\-À-ſ]+)/g;
const SCORE_RE   = /(?:score|puntuaci[oó]n|note)[^\n]{0,60}?(\d{1,3})\s*\/\s*100/i;
const VERDICT_RE = /\*\*\s*(?:verdict|veredicto)\s*:?\s*\*\*\s*:?\s*([^\n]+)/i;

const SCORING_KEYWORDS  = /scoring|score|puntuaci[oó]n|notation/i;
const SOURCES_KEYWORDS  = /aller\s+plus\s+loin|to\s+go\s+further|para\s+ir|sources?(?!\s+utilis)/i;
const SYNTHESE_KEYWORDS = /synth[éèe]se|summary|s[ií]ntesis/i;

/**
 * @param {string} md - The raw markdown emitted by the LLM.
 * @returns {ParsedDiagnostic|null}
 */
export default function parseDiagnostic(md) {
  if (!md || typeof md !== 'string') return null;

  const title = (md.match(TITLE_RE) || [])[1]?.trim() || '';

  const tags = [];
  const tagsLine = (md.match(TAGS_RE) || [])[1] || '';
  for (const m of tagsLine.matchAll(TAG_TOKEN)) {
    const t = m[1].trim();
    if (t && !tags.includes(t)) tags.push(t);
  }

  // Split on level-2 headings. parts[0] = preamble (title, tags).
  const parts = md.split(/^##\s+/m);
  let synthese = null;
  let sources = null;
  const sections = [];

  for (let i = 1; i < parts.length; i++) {
    const lines = parts[i].split('\n');
    const heading = lines.shift().trim();
    const body = lines.join('\n').trim();

    if (SYNTHESE_KEYWORDS.test(heading)) {
      synthese = { title: heading, body, items: parseSyntheseBullets(body) };
      continue;
    }
    if (SOURCES_KEYWORDS.test(heading)) {
      sources = { title: heading, body };
      continue;
    }

    const numMatch = heading.match(/^(\d+)\.\s*(.+)/);
    sections.push({
      number: numMatch ? parseInt(numMatch[1], 10) : null,
      title: numMatch ? numMatch[2].trim() : heading,
      body,
      isScoring: SCORING_KEYWORDS.test(heading),
    });
  }

  const scoringSection = sections.find((s) => s.isScoring);
  let scoring = null;
  if (scoringSection) {
    scoring = parseScoring(scoringSection.body);
  }

  // Extract a 1-line subtitle from the Synthèse → Recommandation, falls
  // back to the verdict if present.
  const recommandation =
    synthese?.items?.find((it) => /recommand/i.test(it.key))?.value || '';
  const subtitle = recommandation || scoring?.verdict || '';

  return { title, tags, synthese, sections, scoring, sources, subtitle };
}

function parseSyntheseBullets(body) {
  // Each bullet looks like:  - **Organisation :** Ministère de l'Agriculture
  const out = [];
  const re = /^[-*]\s+\*\*([^:]+?)\s*:\s*\*\*\s*(.+)$/gm;
  let m;
  while ((m = re.exec(body)) !== null) {
    out.push({ key: m[1].trim(), value: m[2].trim() });
  }
  return out;
}

function parseScoring(body) {
  const criteria = [];
  // Match table rows: | Critère | Score | Analyse |
  const rowRe = /^\|\s*([^|\n]+?)\s*\|\s*(\d+)\s*\/\s*10\s*\|\s*([^|\n]+?)\s*\|/gm;
  let m;
  while ((m = rowRe.exec(body)) !== null) {
    const name = m[1].trim();
    if (/^---+$/.test(name) || /^crit[èe]re$/i.test(name)) continue;
    criteria.push({
      name,
      score: parseInt(m[2], 10),
      analysis: m[3].trim(),
    });
  }
  const globalScore = parseInt((body.match(SCORE_RE) || [])[1], 10) || null;
  const verdict = (body.match(VERDICT_RE) || [])[1]?.trim() || '';
  return { criteria, global: globalScore, verdict };
}
